import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImageOff, Loader2, Minus, Plus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useRegisterBreakdown, useRelatedItems } from './inventory.api';
import { itemLabel, type InventoryItem } from './types';

/**
 * Break a case open, or box units back up.
 *
 * A case and its units are two separate inventory rows joined by `pack` — how
 * many of *this* item one of those is worth. Opening one case adds a case to
 * the related row and takes `pack` units off this one; boxing up does the
 * reverse. The API takes both directions in one payload, per related item.
 *
 * The whole point is that the two counts stay consistent, so the projected
 * result for this item is shown live and the submit is refused once it would
 * go negative — the original let you commit a breakdown that left the unit
 * item below zero and only found out afterwards.
 */

interface Entry {
  add: string;
  remove: string;
}

export function BreakdownDialog({
  item,
  open,
  onOpenChange,
}: {
  item: InventoryItem | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<Record<string, Entry>>({});

  const related = useRelatedItems(open ? (item?.id ?? null) : null);
  const submit = useRegisterBreakdown();

  useEffect(() => {
    setEntries({});
  }, [item?.id, open]);

  const rows = useMemo(() => related.data ?? [], [related.data]);

  const read = (id: string, key: keyof Entry) => entries[id]?.[key] ?? '';
  const set = (id: string, key: keyof Entry, value: string) =>
    setEntries((current) => {
      const existing = current[id] ?? { add: '', remove: '' };
      return { ...current, [id]: { ...existing, [key]: value } };
    });

  const numeric = (value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  /**
   * What this item ends up at.
   *
   * Adding one of a related pack consumes `pack` units of this item; removing
   * one puts them back.
   */
  const projected = useMemo(() => {
    if (!item) return 0;
    return rows.reduce((total, row) => {
      const entry = entries[row.id];
      if (!entry) return total;
      return (
        total -
        numeric(entry.add) * row.pack +
        numeric(entry.remove) * row.pack
      );
    }, item.quantityOnHand);
  }, [rows, entries, item]);

  const payload = rows
    .map((row) => ({
      businessItemId: row.id,
      add: numeric(read(row.id, 'add')),
      remove: numeric(read(row.id, 'remove')),
    }))
    .filter((entry) => entry.add > 0 || entry.remove > 0);

  const wouldGoNegative = projected < 0;
  const canSubmit = payload.length > 0 && !wouldGoNegative;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('inventory.breakdown')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('inventory.breakdownBody', {
              name: item ? itemLabel(item) : '',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {related.isLoading && <Skeleton className="h-32 w-full rounded-xl" />}

        {related.isSuccess && rows.length === 0 && (
          <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
            {t('inventory.noRelated')}
          </p>
        )}

        {rows.length > 0 && (
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex items-center gap-3 rounded-xl border p-3"
              >
                {row.itemImage ? (
                  <img
                    src={row.itemImage}
                    alt=""
                    className="bg-muted size-10 shrink-0 rounded-md object-contain"
                  />
                ) : (
                  <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md">
                    <ImageOff className="size-3.5" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{itemLabel(row)}</p>
                  <p className="text-muted-foreground text-xs">
                    {t('inventory.worthUnits', {
                      count: row.pack,
                    })}{' '}
                    · {t('inventory.hasNow', {
                      quantity: formatNumber(row.quantityOnHand),
                    })}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <label className="text-muted-foreground space-y-1 text-center text-xs">
                    <span className="flex items-center justify-center gap-1">
                      <Plus className="size-3" />
                      {t('inventory.open')}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      className="h-8 w-16 text-center"
                      value={read(row.id, 'add')}
                      onChange={(event) =>
                        set(row.id, 'add', event.target.value)
                      }
                    />
                  </label>
                  <label className="text-muted-foreground space-y-1 text-center text-xs">
                    <span className="flex items-center justify-center gap-1">
                      <Minus className="size-3" />
                      {t('inventory.boxUp')}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      className="h-8 w-16 text-center"
                      value={read(row.id, 'remove')}
                      onChange={(event) =>
                        set(row.id, 'remove', event.target.value)
                      }
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        {rows.length > 0 && item && (
          <p
            className={cn(
              'rounded-xl p-3 text-sm',
              wouldGoNegative
                ? 'bg-destructive/10 text-destructive'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {wouldGoNegative
              ? t('inventory.breakdownNegative', {
                  name: itemLabel(item),
                  to: formatNumber(projected),
                })
              : t('inventory.breakdownEcho', {
                  name: itemLabel(item),
                  from: formatNumber(item.quantityOnHand),
                  to: formatNumber(projected),
                })}
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submit.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!canSubmit || submit.isPending}
            onClick={(event) => {
              event.preventDefault();
              submit.mutate(payload, {
                onSuccess: () => {
                  toast.success(t('inventory.breakdownDone'));
                  onOpenChange(false);
                },
                onError: (error) =>
                  toast.error(
                    error instanceof Error
                      ? error.message
                      : t('inventory.actionFailed')
                  ),
              });
            }}
          >
            {submit.isPending && <Loader2 className="animate-spin" />}
            {t('inventory.applyBreakdown')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
